'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
}
const item = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
}

const trustStats = [
  { value: '$4.2B+', label: 'Loans originated' },
  { value: '0.9%', label: 'APR from' },
  { value: '120s', label: 'Avg. funding' },
  { value: '50+', label: 'Assets accepted' },
]

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-grid animate-gridmove pointer-events-none" />
      <div className="absolute inset-0 bg-radial-glow pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-electric-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-electric-700/10 blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        {/* Left copy */}
        <motion.div variants={container} initial="hidden" animate="visible">
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-xs text-steel-200 mb-7"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-electric-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-electric-400" />
            </span>
            Non-custodial · Audited · Live in 40+ countries
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            <span className="text-chrome">Borrow against</span>
            <br />
            <span className="text-glow-blue">your crypto.</span>
            <br />
            <span className="text-chrome">Keep the upside.</span>
          </motion.h1>

          <motion.p variants={item} className="mt-6 text-lg text-steel-300 max-w-xl leading-relaxed">
            Unlock instant liquidity without selling a single coin. Get a credit line
            backed by Bitcoin, Ethereum and 50+ digital assets — rates from
            <span className="text-electric-300 font-semibold"> 0.9% APR</span>, funded in minutes.
          </motion.p>

          <motion.div variants={item} className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link
              href="/register"
              className="btn-shimmer inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow hover:shadow-glow-lg transition-shadow"
            >
              Get a loan now
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <a
              href="#calculator"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold glass glass-hover text-steel-100"
            >
              Estimate your loan
            </a>
          </motion.div>

          <motion.div variants={item} className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {trustStats.map((s) => (
              <div key={s.label}>
                <div className="font-display font-bold text-2xl text-white">{s.value}</div>
                <div className="text-xs text-steel-400 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="relative flex justify-center"
        >
          {/* Glow ring behind logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full bg-electric-500/20 blur-3xl animate-pulseGlow" />
          </div>

          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="relative"
          >
            <div className="relative w-[300px] h-[300px] sm:w-[380px] sm:h-[380px] rounded-3xl overflow-hidden ring-1 ring-electric-500/30 shadow-glow-lg">
              <Image src="/logo.png" alt="Crypto Credits emblem" fill className="object-cover" priority />
            </div>

            {/* Floating loan card */}
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -left-6 sm:-left-12 top-8 glass rounded-2xl p-4 w-44 shadow-glow-sm"
            >
              <div className="flex items-center gap-2 text-xs text-steel-300">
                <span className="w-6 h-6 rounded-full bg-electric-500/20 flex items-center justify-center text-electric-300 font-bold">₿</span>
                Collateral
              </div>
              <div className="mt-2 font-display font-bold text-lg text-white">1.00 BTC</div>
              <div className="text-[11px] text-steel-400">Locked · Insured</div>
            </motion.div>

            {/* Floating APR card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="absolute -right-4 sm:-right-10 bottom-10 glass rounded-2xl p-4 w-44 shadow-glow-sm"
            >
              <div className="text-xs text-steel-300">Credit available</div>
              <div className="mt-1 font-display font-bold text-lg text-glow-blue">$42,300</div>
              <div className="flex items-center gap-1 text-[11px] text-emerald-400 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Ready to withdraw
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
    </section>
  )
}
